// Daily prompt (docs/02 §2.4, docs/07 §7.7) — a deterministic key/scale (and a
// 4-chord progression for strum instruments) seeded from the calendar date, so
// everyone gets the same "chord of the day" and it changes each day.

import type { ScaleName } from "./types.ts";

const KEYS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const;
const SCALES: ScaleName[] = ["majorPentatonic", "minorPentatonic", "dorian", "minor", "major", "blues"];
const SCALE_LABELS: Record<ScaleName, string> = {
  majorPentatonic: "major pentatonic",
  minorPentatonic: "minor pentatonic",
  dorian: "dorian",
  minor: "minor",
  major: "major",
  blues: "blues",
  chromatic: "chromatic",
};
// Chord roots as scale degrees (loops of four).
const PROGRESSIONS: number[][] = [
  [0, 4, 5, 3],
  [0, 5, 3, 4],
  [0, 3, 4, 0],
  [5, 3, 0, 4],
];

export interface DailyPrompt {
  dateKey: string;
  key: string;
  scale: ScaleName;
  progression: number[];
  label: string;
}

export function dateKeyOf(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

export function dailyPrompt(date: Date): DailyPrompt {
  const dateKey = dateKeyOf(date);
  const seed = hash(dateKey);
  const key = KEYS[seed % KEYS.length]!;
  const scale = SCALES[(seed >>> 4) % SCALES.length]!; // unsigned shift — signed could go negative
  const progression = PROGRESSIONS[(seed >>> 8) % PROGRESSIONS.length]!;
  return { dateKey, key, scale, progression, label: `${key} ${SCALE_LABELS[scale]}` };
}

// FNV-1a, deterministic.
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
